import { useCallback, useState } from "react";
import { toast } from "sonner";
import {
  BulkEditService,
  type BulkEditProgress,
  type BulkEditProgressItem,
} from "../services/bulkEditService";
import { convertFieldValue } from "@/components/shared/UniversalCRMTable/utils/unified-field-renderer";
import type {
  BaseEntity,
  SimpleFieldConfig,
  FieldValue,
} from "../../UniversalCRMTable/types";

const BULK_EDIT_DELAY_MS = 500;

interface BulkEditCallbackParams {
  setProgressItems: (items: BulkEditProgressItem[]) => void;
  setIsProcessing: (processing: boolean) => void;
}

function buildBulkEditCallbacks({ setProgressItems, setIsProcessing }: BulkEditCallbackParams) {
  return {
    delayMs: BULK_EDIT_DELAY_MS,
    onProgress: (progress: BulkEditProgress) => {
      setProgressItems(progress.items);
      setIsProcessing(!progress.isComplete);
    },
    onComplete: (progress: BulkEditProgress) => {
      if (progress.errorCount === 0) {
        toast.success(`Successfully updated ${progress.completedCount} items`);
      } else {
        toast.warning(
          `Updated ${progress.completedCount} items, ${progress.errorCount} failed`
        );
      }
      (window as any).__isBulkEditOperation = false;
    },
    onError: (error: Error, itemId: string) => {
      console.error(`Failed to update item ${itemId}:`, error);
    },
  };
}

export interface UseBulkEditSaveParams<T extends BaseEntity> {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  selectedItems: T[];
  editValue: string;
  applyToAllFiltered: boolean;
  canApplyToAllFiltered: boolean;
  totalFilteredCount?: number;
  filterCriteria?: Record<string, unknown>;
  onBulkEdit: (fieldId: string, value: FieldValue, itemIds: string[]) => Promise<void>;
}

export interface UseBulkEditSaveResult {
  isLoading: boolean;
  isProcessing: boolean;
  showProgressModal: boolean;
  setShowProgressModal: (open: boolean) => void;
  progressItems: BulkEditProgressItem[];
  progressTitle: string;
  handleSave: () => Promise<void>;
}

export function useBulkEditSave<T extends BaseEntity>({
  fieldId,
  fieldConfig,
  selectedItems,
  editValue,
  applyToAllFiltered,
  canApplyToAllFiltered,
  totalFilteredCount,
  filterCriteria,
  onBulkEdit,
}: UseBulkEditSaveParams<T>): UseBulkEditSaveResult {
  const [isLoading, setIsLoading] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressItems, setProgressItems] = useState<BulkEditProgressItem[]>([]);
  const [progressTitle, setProgressTitle] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSave = useCallback(async () => {
    if (!editValue && fieldConfig.type !== "boolean") {
      toast.error("Please enter a value");
      return;
    }

    setIsLoading(true);
    setIsProcessing(true);
    setShowProgressModal(true);

    try {
      const convertedValue = convertFieldValue(editValue, fieldConfig);
      const targetCount =
        applyToAllFiltered && canApplyToAllFiltered ? totalFilteredCount : selectedItems.length;
      setProgressTitle(`Updating ${fieldConfig.label} for ${targetCount} items`);

      const callbacks = buildBulkEditCallbacks({ setProgressItems, setIsProcessing });

      if (applyToAllFiltered && canApplyToAllFiltered) {
        await BulkEditService.executeBulkEditAllFiltered(
          async (_itemIds: string[], _fieldId: string, _value: FieldValue) => {
            // This would be your GraphQL mutation for bulk update
            // For now, we'll simulate individual updates
            const items = selectedItems; // In real implementation, fetch from server
            return items;
          },
          fieldId,
          convertedValue,
          filterCriteria,
          callbacks
        );
      } else {
        await BulkEditService.executeBulkEdit(
          selectedItems,
          async (item: T, fId: string, value: FieldValue) => {
            await onBulkEdit(fId, value, [item.id]);
          },
          fieldId,
          convertedValue,
          callbacks
        );
      }
    } catch (error) {
      console.error("Bulk edit error:", error);
      toast.error("Failed to update items");
      setShowProgressModal(false);
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  }, [
    editValue,
    fieldConfig,
    applyToAllFiltered,
    canApplyToAllFiltered,
    filterCriteria,
    totalFilteredCount,
    onBulkEdit,
    fieldId,
    selectedItems,
  ]);

  return {
    isLoading,
    isProcessing,
    showProgressModal,
    setShowProgressModal,
    progressItems,
    progressTitle,
    handleSave,
  };
}
